// Phase 7 commands: changing a shipment after it was sold, giving money back, charging at the
// destination, and merging two records of one customer (04 §4.3, §4.6). The documents they leave
// behind are described at the top of `../shipmentChanges.ts`.
import { z } from 'zod';
import { defineCommand } from '../define';
import { currencySchema, slugSchema } from '../config';
import {
  addressSchema,
  barcodeSchema,
  customerIdSchema,
  e164Schema,
  packingItemSchema,
  trackingNoSchema,
} from '../cargo';
import { DESTINATION_CHARGE_KINDS, REFUND_METHODS } from '../shipmentChanges';
import { collectBoxSchema } from './counter';

const minor = z.number().int().nonnegative().max(1e12);
const positiveMinor = minor.refine((n) => n > 0, 'Amount must be more than zero');

/** A person's words for why: kept on the change log, the credit note and the customer's record. */
const reason = z.string().trim().min(3).max(300);

/**
 * What an amendment may change. Everything that is sent replaces what was there; what is left out
 * stays. Anything that touches the price (the destination, the service, the boxes, the add-ons, the
 * declared value) makes the server price the shipment again from the rate card.
 */
export const amendChangesSchema = z
  .object({
    /** Only the fields given are changed. */
    consignee: z
      .object({
        name: z.string().trim().min(1).max(120).optional(),
        phones: z.array(e164Schema).min(1).max(3).optional(),
        address: addressSchema.optional(),
        relationship: z.string().max(40).optional(),
      })
      .strict()
      .optional(),
    destinationCityId: slugSchema.optional(),
    service: z.enum(['sea', 'air']).optional(),
    boxes: z
      .object({
        /** New boxes, exactly as `collectCargo` takes them (`boxSoldNow` sells an empty box with them). */
        add: z.array(collectBoxSchema).max(50).default([]),
        remove: z.array(barcodeSchema).max(50).default([]),
      })
      .strict()
      .optional(),
    /** The add-on codes the shipment should carry from now on (the whole list, not the difference). */
    addOns: z
      .array(z.string().regex(/^[a-z_]{2,30}$/))
      .max(20)
      .optional(),
    declaredValueMinor: minor.optional(),
    packingList: z.array(packingItemSchema).max(200).optional(),
    note: reason,
  })
  .strict();
export type AmendChanges = z.infer<typeof amendChangesSchema>;

/**
 * Changes a shipment before its boxes leave the origin branch (04 §4.3). The server prices it again;
 * a difference becomes a debit or credit note (when the invoice was issued) and a journal, and the
 * balance and the reserves follow.
 */
export const amendShipment = defineCommand({
  type: 'amendShipment',
  permission: 'cargo.amend',
  principals: [],
  offline: true,
  payload: z.object({ shipmentId: trackingNoSchema, changes: amendChangesSchema }).strict(),
});

/** Where the money goes back from. `walletId` is needed for every method except `credit`. */
export const refundInstructionSchema = z
  .object({
    method: z.enum(REFUND_METHODS),
    walletId: z.string().min(1).max(40).optional(),
    amountMinor: positiveMinor,
  })
  .strict();
export type RefundInstruction = z.infer<typeof refundInstructionSchema>;

/**
 * Cancels a shipment while every box is still at a branch of the origin country (04 §4.3). The
 * invoice is credited in full, the boxes and their barcodes are voided, the reserves are released,
 * the points still pending are cancelled, vouchers paid with come back, and what was paid goes back
 * as `refund` (a refund above the approval threshold waits for a second person) or, by default, as
 * customer credit.
 */
export const cancelShipment = defineCommand({
  type: 'cancelShipment',
  permission: 'cargo.cancel',
  principals: [],
  offline: false,
  payload: z
    .object({
      shipmentId: trackingNoSchema,
      reason,
      refund: refundInstructionSchema.optional(),
    })
    .strict(),
});

/**
 * Gives money back for a payment (04 §4.6): `reducesRevenue: false` returns money the customer
 * overpaid (what the shipment is overpaid by, then the part kept as credit); `true` is a dispute
 * settlement: a credit note lowers the invoice by the amount and the money goes back. Above the
 * approval threshold the refund waits for a second person.
 */
export const refundPayment = defineCommand({
  type: 'refundPayment',
  permission: 'finance.refund',
  principals: [],
  offline: false,
  payload: z
    .object({
      paymentId: z.string().min(1).max(40),
      amountMinor: positiveMinor,
      method: z.enum(REFUND_METHODS),
      walletId: z.string().min(1).max(40).optional(),
      reason,
      reducesRevenue: z.boolean().default(false),
    })
    .strict(),
});

/**
 * Puts a charge on a shipment that the consignee pays in the destination currency (04 §4.6): the
 * driver collects it at the door (`deliverStop`) or a partner collects it (`partnerUpdate`). Posted
 * by the destination country: Dr 1100, Cr 4010, or Cr 2040 for customs duties.
 */
export const addDestinationCharge = defineCommand({
  type: 'addDestinationCharge',
  permission: 'cargo.amend',
  alsoPermitted: ['finance.payment.record'],
  principals: [],
  offline: true,
  payload: z
    .object({
      shipmentId: trackingNoSchema,
      kind: z.enum(DESTINATION_CHARGE_KINDS),
      amountMinor: positiveMinor,
      /** Must be the destination country's currency. */
      currency: currencySchema,
      note: z.string().trim().max(300).default(''),
    })
    .strict(),
});

/**
 * Moves everything of `mergeId` to `keepId` and leaves `mergeId` as a record that points at it
 * (04 §4.3). A big merge continues in the same command after it commits; send the command again
 * (same customers) to finish one that was cut short.
 */
export const mergeCustomers = defineCommand({
  type: 'mergeCustomers',
  permission: 'customers.merge',
  principals: [],
  offline: false,
  payload: z.object({ keepId: customerIdSchema, mergeId: customerIdSchema, reason }).strict(),
});

export const CHANGE_COMMANDS = {
  amendShipment,
  cancelShipment,
  refundPayment,
  addDestinationCharge,
  mergeCustomers,
} as const;
